import { useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import { History, Plus } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import styled from 'styled-components'

import { AgentChatPanel } from './AgentChatPanel'
import { AgentHeader } from './AgentHeader'
import { TagMentionProvider } from './chat/useTagMention'
import { requestDock } from './dock/dockState'
import type { ActiveModel } from './modelCard'
import { useActiveModel } from './useActiveModel'
import { useAgentChat } from './useAgentChat'
import { useAgentModels } from './useAgentModels'
import { useConversations } from './useConversations'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`

/** A chart range can run past today; the agent must never be told it does. */
const clampToNow = (iso: string | undefined): string | undefined => {
  if (!iso) return iso
  const stamp = Date.parse(iso)
  return Number.isFinite(stamp) && stamp > Date.now()
    ? new Date().toISOString()
    : iso
}

/**
 * The Agent tab: the conversation, plus one header naming the model currently
 * drawn on the chart. Everything ABOUT a model — its parameters, its alert rule,
 * its backtest — lives in the model card in the bottom dock, opened from the pen
 * icon in the header's dropdown. The sidebar stays a place to talk.
 */
export const AgentTab: React.FC = () => {
  const { t } = useLingui()
  const { siteID, selectedSensors, timeRange } = useScatterplotContext()
  const { models, fetchSeries, deleteModel } = useAgentModels(siteID)
  const {
    active,
    publish,
    clear,
    kpiOnly,
    setKpiOnly,
    hidablePenCount,
    showResidual,
    setShowResidual,
    canShowResidual,
  } = useActiveModel(siteID, fetchSeries)

  // What the user has plotted, so "this tag" / "this window" resolve for the
  // agent. While a model is on the chart those pens ARE the model's inputs.
  const viewer = useMemo(
    () => ({
      tags: selectedSensors.map((sensor) => sensor.id),
      from: timeRange?.from,
      to: clampToNow(timeRange?.to),
      // Whatever model is drawn, dropdown pick or fresh build alike, so the
      // agent can act on it without going looking for what is already on screen.
      model: active
        ? {
            entry_id: active.entry_id,
            name: active.name,
            kpi: active.kpi,
            expr: active.expr,
            gate_expr: active.gate_expr,
            drivers: active.drivers,
            baseline_window: active.baseline_window,
            sample_minutes: (active as { sample_minutes?: number })
              .sample_minutes,
            grade_minutes: (active as { grade_minutes?: number }).grade_minutes,
            last30_sigma: active.last30_sigma,
            unsaved: active.unsaved,
          }
        : undefined,
    }),
    [selectedSensors, timeRange, active]
  )

  const conversations = useConversations(siteID)
  const chat = useAgentChat(siteID, conversations, viewer)
  const [showHistory, setShowHistory] = useState(false)

  return (
    <Container>
      <AgentHeader
        models={models}
        active={active}
        onView={(model) => void publish(model as ActiveModel)}
        onOpenCard={() => requestDock('config')}
        onOpenDetail={(model) => {
          // Draw it, then open its card: the card edits the model on the chart,
          // so the two must not disagree about which model that is.
          void publish(model as ActiveModel)
          requestDock('config')
        }}
        onDelete={(model) => {
          void deleteModel(model.entry_id).then((ok) => {
            if (ok && active?.entry_id === model.entry_id) clear()
          })
        }}
        onCloseView={clear}
        kpiOnly={kpiOnly}
        onKpiOnlyChange={setKpiOnly}
        hidablePenCount={hidablePenCount}
        showResidual={showResidual}
        onShowResidualChange={setShowResidual}
        canShowResidual={canShowResidual}
        trailing={
          <>
            <Button
              variant='ghostMuted'
              size='icon-sm'
              aria-pressed={showHistory}
              aria-label={t`Past conversations`}
              title={
                showHistory ? t`Hide past conversations` : t`Past conversations`
              }
              onClick={() => setShowHistory((prev) => !prev)}>
              <History />
            </Button>
            <Button
              variant='ghostMuted'
              size='icon-sm'
              aria-label={t`New conversation`}
              title={t`Start a new conversation`}
              disabled={!chat.turns.length && !chat.conversationId}
              onClick={chat.startNew}>
              <Plus />
            </Button>
          </>
        }
      />
      <TagMentionProvider>
        <AgentChatPanel
          siteId={siteID}
          chat={chat}
          conversations={conversations}
          showHistory={showHistory}
          onShowHistoryChange={setShowHistory}
        />
      </TagMentionProvider>
    </Container>
  )
}
